import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { PersonService } from "../../../../services/person.service";
import { OfficeService } from "../../../../services/office.service";
import { ToastrService } from "ngx-toastr";
import { ContractType, EmployeeRequest, PersonType } from "../../employee.model";
import { EmployeeService } from "../../services/employee.service";
import { ResponsibilitiesService } from "src/app/features/responsibilities/services/responsibilities.service";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
  selector: "app-employee-form",
  templateUrl: "./employee-form.component.html",
  styleUrls: ["./employee-form.component.css"],
})
export class EmployeeFormComponent implements OnInit {
  isSaving = false;
  showCelebration = true;
  imageUrl: string | ArrayBuffer | null = null;
  selectedFile: File | null = null;
  formGroup: FormGroup;
  offices: any[] = [];
  responsibilities: any[] = [];
  managers: any[] = [];
  supervisors: any[] = [];
  roles: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private personService: PersonService,
    private officeService: OfficeService,
    private responsibilitiesService: ResponsibilitiesService,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private employeeService: EmployeeService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.getOffices();
    this.getManagers();
    this.getSupervisors();
    this.loadInitialData();
  }

  initializeForm(): void {
    this.formGroup = this.fb.group({
      id: [""],
      username: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      cep: [""],
      city: [""],
      complement: [""],
      neighborhood: [""],
      streetName: [""],
      uf: [""],
      phone: [""],
      cellphone: [""],
      name: ["", Validators.required],
      cpfCnpj: [""],
      picture: [null],
      birthdate: [""],
      gender: [""],
      contractType: [""],
      personType: [""],
      officeId: ["", Validators.required],
      office: [],
      responsibility: [],
      responsibilityId: ["", Validators.required],
      managerId: [""],
      supervisorId: [""],
      accessType: ["", Validators.required],
    });
  }

  loadInitialData(): void {
    const id = this.route.snapshot.params["id"];
    if (id) {
      this.personService.findById(id).subscribe({
        next: (response) => {
          this.formGroup.get("id").patchValue(id);
          this.formGroup.get("name").patchValue(response.name);
          this.formGroup.get("email").patchValue(response.user.email);
          this.formGroup.get("cpfCnpj").patchValue(response.cpfCnpj);
          this.formGroup.get("birthdate").patchValue(response.birthdate);
          this.formGroup.get("contractType").patchValue(response.contractType);
          this.formGroup.get("personType").patchValue(response.personType);
          this.formGroup.get("username").patchValue(response.user.username);
          this.formGroup.get("gender").patchValue(response.gender);
          this.formGroup.get("officeId").patchValue(response.officeId);
          this.getResponsibilities(response.officeId);
          this.formGroup.get("responsibilityId").patchValue(response.responsibilityId);
          this.formGroup.get("accessType").patchValue(response.accessType);
          this.formGroup.get("managerId").patchValue(response.managerId);
          this.formGroup.get("supervisorId").patchValue(response.supervisorId);
          this.imageUrl = response.picture;
        },
        error: () => this.toast.error("Erro ao carregar funcionário para edição. Tente novamente mais tarde."),
      });
    }

    // Subscription de CEP removida - endereço não é mais necessário
  }

  onSubmit() {
    this.isSaving = true;

    if (this.formGroup.valid) {
      switch (this.formGroup.get("accessType")?.value) {
        case "Manager":
          this.formGroup.get("personType").patchValue(PersonType.MANAGER);
          break;
        case "Supervisor":
          this.formGroup.get("personType").patchValue(PersonType.SUPERVISOR);
          break;
        case "User":
          this.formGroup.get("personType").patchValue(PersonType.USER);
          break;
        default:
          this.formGroup.get("personType").patchValue(PersonType.USER);
      }

      const employee: EmployeeRequest = {
        name: this.formGroup.get("name")?.value,
        birthdate: this.formGroup.get("birthdate")?.value,
        cpfCnpj: this.formGroup.get("cpfCnpj")?.value,
        gender: this.formGroup.get("gender")?.value,
        officeId: this.formGroup.get("officeId")?.value,
        responsibilityId: this.formGroup.get("responsibilityId")?.value,
        managerId: this.formGroup.get("managerId")?.value,
        supervisorId: this.formGroup.get("supervisorId")?.value,
        accessType: this.formGroup.get("accessType")?.value,
        personType: this.formGroup.get("personType")?.value,
        contractType: ContractType.CLT,
        username: this.formGroup.get("username")?.value,
        email: this.formGroup.get("email")?.value,
        roles: this.roles,
      };

      if (this.formGroup.get("id").value != "") {
        // update
        this.employeeService.update(this.formGroup.get("id").value, employee, this.selectedFile).subscribe({
          next: () => {
            this.router.navigate(["/funcionarios"]).then((success) => {
              if (success) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            });
            this.isSaving = false;
            this.toast.success("🎉 Funcionário alterado com sucesso!");
          },
          error: (error: HttpErrorResponse) => {
            this.isSaving = false;
            this.toast.error(error.error?.message ?? "Erro ao atualizar funcionário. Tente novamente mais tarde.");
          },
          complete: () => {
            this.formGroup.reset();
          },
        });
      } else {
        // create
        this.employeeService.create(employee, this.selectedFile).subscribe({
          next: () => {
            this.router.navigate(["/funcionarios"]).then((success) => {
              if (success) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            });
            this.isSaving = false;
            this.toast.success("🎉 Funcionário salvo com sucesso!");
          },
          error: (error: HttpErrorResponse) => {
            this.isSaving = false;
            this.toast.error(error.error?.message ?? "Erro ao salvar funcionário. Tente novamente mais tarde.");
          },
          complete: () => {
            this.formGroup.reset();
          },
        });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.toast.error("O formulário para cadastro não é válido. Alguma informação deve estar errada.");
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];

    if (file) {
      this.selectedFile = file;
      this.formGroup.patchValue({
        photo: file,
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result;
      };
      reader.readAsDataURL(file);
    }
  }

  getOffices() {
    this.officeService.findAll().subscribe({
      next: (response: any[]) => {
        this.offices = response;
      },
      error: () => {
        this.toast.error("Erro ao carregar empresas. Tente novamente mais tarde.");
      },
    });
  }

  getResponsibilities(officeId: string) {
    this.responsibilitiesService.findByOffice(officeId).subscribe({
      next: (response: any[]) => (this.responsibilities = response),
      error: () => this.toast.error("Erro ao carregar responsabilidades. Tente novamente mais tarde."),
    });
  }

  getManagers(): void {
    this.personService.findAllManagers().subscribe({
      next: (response: any[]) => (this.managers = response),
      error: () => this.toast.error("Erro ao carregar gestores. Tente novamente mais tarde."),
    });
  }

  getSupervisors(): void {
    this.personService.findAllSupervisors().subscribe({
      next: (response: any[]) => (this.supervisors = response),
      error: () => this.toast.error("Erro ao carregar supervisores. Tente novamente mais tarde."),
    });
  }

  onOfficeChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("officeId").patchValue(value);

    // Limpar o cargo quando a empresa mudar
    this.formGroup.get("responsibilityId").patchValue("");

    // Carregar responsabilidades da nova empresa
    if (value) {
      this.getResponsibilities(value);
    } else {
      // Se não houver empresa selecionada, limpar a lista de responsabilidades
      this.responsibilities = [];
    }
  }

  onResponsibilityChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("responsibilityId").patchValue(value);
  }

  onManagerChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("managerId").patchValue(value);
  }

  onSupervisorChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("supervisorId").patchValue(value);
  }

  onInputChange(): void {
    const usernameControl = this.formGroup.get("username");
    usernameControl?.setValue(usernameControl.value.replace(/\s/g, ""));
  }
}
